/**
 * PIN-based share encryption.
 *
 * Client-side AES-256-GCM with a key derived from the PIN via PBKDF2-SHA256.
 * Runs identically in the desktop (Node) and the viewer (browser) via the Web
 * Crypto API. The server only ever sees the {@link EncryptedShareEnvelope}, so a
 * public GET of a protected share reveals nothing without the PIN.
 */
import type { EncryptedShareEnvelope, SharePayloadV1 } from './types.ts';

/** PBKDF2 work factor — high enough to make offline PIN brute-force slow. */
export const PBKDF2_ITERATIONS = 210_000;
const SALT_BYTES = 16;
const IV_BYTES = 12;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/** Thrown when decryption fails — wrong PIN or a corrupted/tampered envelope. */
export class ShareDecryptError extends Error {
  constructor(message = 'Incorrect PIN or corrupted share') {
    super(message);
    this.name = 'ShareDecryptError';
  }
}

function getCrypto(): Crypto {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c?.subtle) {
    throw new Error('Web Crypto API is not available in this environment');
  }
  return c;
}

function randomBytes(n: number): Uint8Array<ArrayBuffer> {
  const b = new Uint8Array(n);
  getCrypto().getRandomValues(b);
  return b;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

function fromBase64(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

async function deriveKey(
  pin: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
): Promise<CryptoKey> {
  const subtle = getCrypto().subtle;
  const keyMaterial = await subtle.importKey(
    'raw',
    new Uint8Array(textEncoder.encode(pin)),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Encrypt a share payload with a PIN, producing a storable envelope. */
export async function encryptShare(
  payload: SharePayloadV1,
  pin: string,
): Promise<EncryptedShareEnvelope> {
  if (!pin) throw new Error('PIN is required to encrypt a share');
  const salt = randomBytes(SALT_BYTES);
  const iv = randomBytes(IV_BYTES);
  const key = await deriveKey(pin, salt, PBKDF2_ITERATIONS);
  const plaintext = new Uint8Array(textEncoder.encode(JSON.stringify(payload)));
  const ciphertext = new Uint8Array(
    await getCrypto().subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext),
  );
  return {
    enc: 'aes-256-gcm',
    kdf: 'pbkdf2-sha256',
    iterations: PBKDF2_ITERATIONS,
    salt: toBase64(salt),
    iv: toBase64(iv),
    ciphertext: toBase64(ciphertext),
  };
}

/** Decrypt a share envelope with a PIN. Throws {@link ShareDecryptError} on a
 *  wrong PIN (AES-GCM authentication fails). */
export async function decryptShare(
  envelope: EncryptedShareEnvelope,
  pin: string,
): Promise<SharePayloadV1> {
  const salt = fromBase64(envelope.salt);
  const iv = fromBase64(envelope.iv);
  const key = await deriveKey(pin, salt, envelope.iterations);
  let plainBuffer: ArrayBuffer;
  try {
    plainBuffer = await getCrypto().subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      fromBase64(envelope.ciphertext),
    );
  } catch {
    throw new ShareDecryptError();
  }
  try {
    return JSON.parse(textDecoder.decode(plainBuffer)) as SharePayloadV1;
  } catch {
    throw new ShareDecryptError('Decrypted payload was not valid share JSON');
  }
}
